/**
 * ============================================
 * ROUTES - Configuración de Rutas
 * ============================================
 *
 * Este archivo define todas las rutas de la aplicación.
 * Cada ruta mapea una URL a un componente de página.
 *
 * CONCEPTOS CLAVE:
 *
 * 1. createBrowserRouter: Crea el router usando la API del navegador
 *    (history.pushState). Esto permite URLs limpias como /pacientes
 *
 * 2. path: La URL que activa esta ruta
 *    - "/" = página principal
 *    - "/pacientes" = página de pacientes
 *
 * 3. element: El componente React que se renderiza
 *
 * 4. children: Rutas anidadas que comparten el mismo layout
 *    Esto evita repetir el AppLayout en cada página
 *
 * 5. Outlet: Componente especial donde se renderizan las rutas hijas
 *
 * 6. ProtectedRoute: Componente que verifica autenticación antes de
 *    mostrar el contenido. Si no está autenticado, redirige a /login.
 */

import { createBrowserRouter } from "react-router-dom";

// Layout principal
import { RootLayout } from "@/components/layout";

// Componente de protección de rutas
import { ProtectedRoute } from "@/components/protected-route";

// Páginas
import DashboardPage from "@/pages/dashboard";
import TurnosPage from "@/pages/turnos";
import PantallaPublicaTurnos from "@/pages/turnos/pantalla-publica";
import KioskoTurnos from "@/pages/turnos/kiosko-turnos";
import PacientesPage from "@/pages/pacientes";
import MedicosRemitentesPage from "@/pages/medicos-remitentes";
import OrdenesPage from "@/pages/ordenes";
import ExamenesPage from "@/pages/examenes";
import PerfilesPage from "@/pages/perfiles";
import FacturacionPage from "@/pages/facturacion";
import CAIPage from "@/pages/facturacion/cai";
import ListasPreciosPage from "@/pages/facturacion/listas-precios";
import MuestrasPage from "@/pages/muestras";
import AtencionPage from "@/pages/muestras/atencion";
import ProcesamientoPage from "@/pages/procesamiento";
import ValidacionPage from "@/pages/validacion";
import InformesPage from "@/pages/informes";
import CajaPage from "@/pages/caja";
import InventarioPage from "@/pages/inventario";
import UsuariosPage from "@/pages/usuarios";
import SucursalesPage from "@/pages/sucursales";
import PuntosAtencionPage from "@/pages/puntos-atencion";
import ClientesPage from "@/pages/clientes";
import ConfiguracionPage from "@/pages/configuracion";
import TiposContenedorPage from "@/pages/tipos-contenedor";
import { LoginPage } from "@/pages/auth";

/**
 * Router de la aplicación
 *
 * Estructura:
 * - /login: Ruta pública (sin autenticación)
 * - /: Ruta protegida con RootLayout (requiere autenticación)
 *   - Todas las páginas internas son hijas de esta ruta
 *
 * ProtectedRoute envuelve el RootLayout para que:
 * 1. Si no hay sesión → redirige a /login
 * 2. Si hay sesión → muestra el layout con sidebar y contenido
 */
export const router = createBrowserRouter([
  {
    // Ruta de login - PÚBLICA (sin ProtectedRoute)
    path: "/login",
    element: <LoginPage />,
  },
  {
    // Pantalla de turnos - PROTEGIDA (requiere autenticación)
    // Para mostrar en TVs de sala de espera con usuario dedicado
    path: "/pantalla-turnos/:sucursalCodigo",
    element: (
      <ProtectedRoute>
        <PantallaPublicaTurnos />
      </ProtectedRoute>
    ),
  },
  {
    // Kiosko de turnos - PROTEGIDO (requiere autenticación)
    // Para tablets de autoservicio con usuario dedicado
    path: "/kiosko-turnos/:sucursalCodigo",
    element: (
      <ProtectedRoute>
        <KioskoTurnos />
      </ProtectedRoute>
    ),
  },
  {
    // Ruta raíz - PROTEGIDA (requiere autenticación)
    path: "/",
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    // Todas las páginas son hijas de esta ruta protegida
    children: [
      {
        // index: true significa que es la ruta por defecto de "/"
        index: true,
        element: <DashboardPage />,
      },
      {
        path: "turnos",
        element: <TurnosPage />,
      },
      {
        path: "pacientes",
        element: <PacientesPage />,
      },
      {
        path: "medicos-remitentes",
        element: <MedicosRemitentesPage />,
      },
      {
        path: "ordenes",
        element: <OrdenesPage />,
      },
      {
        path: "examenes",
        element: <ExamenesPage />,
      },
      {
        path: "perfiles",
        element: <PerfilesPage />,
      },
      {
        path: "facturacion",
        element: <FacturacionPage />,
      },
      {
        path: "facturacion/cai",
        element: <CAIPage />,
      },
      {
        path: "facturacion/listas-precios",
        element: <ListasPreciosPage />,
      },
      {
        path: "muestras",
        element: <MuestrasPage />,
      },
      {
        path: "muestras/atencion/:id",
        element: <AtencionPage />,
      },
      {
        path: "procesamiento",
        element: <ProcesamientoPage />,
      },
      {
        path: "validacion",
        element: <ValidacionPage />,
      },
      {
        path: "informes",
        element: <InformesPage />,
      },
      {
        path: "caja",
        element: <CajaPage />,
      },
      {
        path: "inventario",
        element: <InventarioPage />,
      },
      {
        path: "usuarios",
        element: <UsuariosPage />,
      },
      {
        path: "sucursales",
        element: <SucursalesPage />,
      },
      {
        path: "puntos-atencion",
        element: <PuntosAtencionPage />,
      },
      {
        path: "clientes",
        element: <ClientesPage />,
      },
      {
        path: "configuracion",
        element: <ConfiguracionPage />,
      },
      {
        path: "tipos-contenedor",
        element: <TiposContenedorPage />,
      },
    ],
  },
]);
